/** CrmNotesSection (Item E, slice E1) — the profile Notes card (spec §7.3): newest-first previews
 * (author · date · preview · link count) + "New note", above the timeline on person/org profiles.
 * Owns the note-overlay open state via the `note` selection param (§7.6: open = push, Back closes to
 * the profile; the param drops when the profile changes). Built panel-ready so D6 can mount the same
 * components for the list_entry (opportunity) Notes surface. */
import { useEffect, useRef, useState } from 'react';

import { useSelectionParam } from '../../hooks/navigation/useUrlState';
import { useEntityNotes } from '../../hooks/useCrm';
import { CrmNoteOverlay, type CrmNoteHostKind } from './CrmNoteOverlay';
import type { MentionContextTarget } from './crmNoteMention';
import { fmtShortDate } from './crmFormat';
import styles from './crm.module.css';

export function CrmNotesSection({ kind, id, hostLabel, mentionContext }: {
  kind: CrmNoteHostKind;
  id: number;
  hostLabel?: string | null;
  mentionContext?: MentionContextTarget[];
}) {
  const [limit, setLimit] = useState(20);
  const notes = useEntityNotes(kind, id, { limit });
  const { value: noteParam, open, close } = useSelectionParam('note');

  // §7.6: dropping the note param when the PROFILE changes (not on mount, so a deep-linked note still
  // opens). prevKey tracks the host identity; the overlay only closes on an actual profile switch.
  const prevKey = useRef(`${kind}:${id}`);
  useEffect(() => {
    const key = `${kind}:${id}`;
    if (prevKey.current !== key) {
      prevKey.current = key;
      if (noteParam) close();
    }
  }, [kind, id, noteParam, close]);

  const items = notes.data?.items ?? [];

  return (
    <section className={styles.notesSection}>
      <div className={styles.notesHead}>
        <h3 className={styles.notesTitle}>Notes</h3>
        <button type="button" className={styles.notesNewBtn} onClick={() => open('new')}>+ New note</button>
      </div>
      {notes.isLoading ? (
        <div className={styles.notesEmpty}>Loading…</div>
      ) : items.length === 0 ? (
        <div className={styles.notesEmpty}>No notes yet.</div>
      ) : (
        <ul className={styles.notesList}>
          {items.map((n) => (
            <li key={n.id} className={styles.noteRow}>
              <button type="button" className={styles.noteRowBtn} onClick={() => open(String(n.id))}>
                <div className={styles.noteRowPreview}>{n.preview_text || '(empty)'}</div>
                <div className={styles.noteRowMeta}>
                  {/* No author == a SYSTEM note published from an auto-accepted AI block (S5c);
                      create_note always stamps an author, so null can only mean that. Attribute it
                      as 'AI' (the panel's own vocabulary) — 'Someone' would read like a bug. */}
                  {n.author?.name ?? (n.author === null ? 'AI' : 'Someone')}
                  {/* The SHARED formatter (spec §3): `new Date(...).toLocaleDateString()` read
                      the wire's naive UTC as browser-local and printed in the browser's zone,
                      so this note's date could disagree with the same note's date on the
                      timeline two cards away. */}
                  {n.created_at ? ` · ${fmtShortDate(n.created_at)}` : ''}
                  {n.link_count > 1 ? ` · ${n.link_count} links` : ''}
                  {n.visibility_mode === 'author_only' ? ' · Only me' : ''}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {notes.data?.has_more && (
        <button type="button" className={styles.notesLoadMore}
          disabled={notes.isFetching} onClick={() => setLimit((n) => Math.min(n + 20, 100))}>
          {notes.isFetching ? 'Loading…' : 'Load 20 more'}
        </button>
      )}
      {noteParam && (
        <CrmNoteOverlay
          noteParam={noteParam}
          hostKind={kind}
          hostId={id}
          hostLabel={hostLabel}
          mentionContext={mentionContext}
          onClose={close}
          onCreated={(nid) => open(String(nid), { replace: true })}
        />
      )}
    </section>
  );
}
